import { memo, useCallback } from 'react'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import StarRating from './StarRating'
import { formatRelativeTime } from '../../utils/dateFormatting'

/**
 * ReviewCard - Single review with optional owner edit/delete controls
 * @idempotent @atomic
 */
const ReviewCard = memo(function ReviewCard({ review, currentUserId, isAdmin, onEdit, onDelete, t }) {
  const userId = review.userId || review.user_id
  const isOwner = !!(currentUserId && userId === currentUserId)
  const canManage = isOwner || !!isAdmin
  const isPending = review.moderationStatus === 'pending' || review.moderation_status === 'pending'

  // Backend returns nested user object: { user: { firstName, lastName, avatar } }
  const u = review.user || {}
  const rawName = review.userName || review.user_name ||
    [u.firstName || u.first_name, u.lastName || u.last_name].filter(Boolean).join(' ')
  const displayName = rawName || t('common.anonymous', 'Anonymous')
  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  const avatarUrl = review.avatarUrl || review.avatar_url || u.avatar || u.avatarUrl

  const handleEdit = useCallback((e) => {
    e.preventDefault()
    onEdit?.(review)
  }, [onEdit, review])

  const handleDelete = useCallback((e) => {
    e.preventDefault()
    onDelete?.(review.id)
  }, [onDelete, review.id])

  return (
    <div className="py-3 border-b border-primary/10 last:border-0">
      <div className="flex items-start gap-2">
        {avatarUrl
          ? <img src={avatarUrl} alt={displayName} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
          : (
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
              {initials}
            </div>
          )
        }
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1 mb-0.5">
            <div className="min-w-0">
              <span className="text-xs font-semibold text-gray-900 truncate block">{displayName}</span>
              <span className="text-[11px] text-gray-400">{formatRelativeTime(review.createdAt || review.created_at)}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {canManage && (
                <>
                  <button
                    type="button"
                    onClick={handleEdit}
                    onTouchEnd={handleEdit}
                    className="p-0.5 rounded text-gray-400 hover:text-primary transition-colors"
                    aria-label={t('review.editReview', 'Edit')}
                  >
                    <PencilIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    onTouchEnd={handleDelete}
                    className="p-0.5 rounded text-gray-400 hover:text-red-500 transition-colors"
                    aria-label={t('review.deleteReview', 'Delete')}
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
          {review.rating != null && (
            <StarRating rating={review.rating} readonly size="sm" className="mb-1" />
          )}
          {(isOwner || !!isAdmin) && isPending && (
            <span className="inline-block text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded mb-1">
              {t('review.pendingApproval', 'Pending approval')}
            </span>
          )}
          {review.content && (
            <p className="text-xs text-gray-700 leading-relaxed">{review.content}</p>
          )}
        </div>
      </div>
    </div>
  )
})

export default ReviewCard
